import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MapPin, Calendar, Image as ImageIcon, User } from "lucide-react";
import { ArticleFormData } from "@/types/article";

interface ArticleFormFieldsProps {
  formData: ArticleFormData;
  setFormData: (data: ArticleFormData) => void;
  isLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  isSpecial?: boolean;
}

export const ArticleFormFields = ({
  formData,
  setFormData,
  isLoading,
  onSubmit,
  isSpecial,
}: ArticleFormFieldsProps) => {
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="title" className="text-elegant-dark text-lg">
          タイトル
        </Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
          className="border-elegant/20 focus:border-elegant"
          placeholder="記事のタイトルを入力してください"
        />
      </div>

      {!isSpecial && (
        <div className="space-y-2">
          <Label htmlFor="category" className="text-elegant-dark text-lg">
            カテゴリー
          </Label>
          <Input
            id="category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            required
            className="border-elegant/20 focus:border-elegant"
            placeholder="例: 祭り、B級グルメ、伝統行事など"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="sub_category" className="text-elegant-dark text-lg flex items-center gap-2">
          <MapPin className="w-5 h-5 text-elegant" />
          サブカテゴリー
        </Label>
        <Input
          id="sub_category"
          value={formData.sub_category || ""}
          onChange={(e) => setFormData({ ...formData, sub_category: e.target.value })}
          className="border-elegant/20 focus:border-elegant"
          placeholder="(任意)サブカテゴリーを入力してください"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="location" className="text-elegant-dark text-lg flex items-center gap-2">
          <MapPin className="w-5 h-5 text-elegant" />
          開催場所
        </Label>
        <Input
          id="location"
          value={formData.location || ""}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          className="border-elegant/20 focus:border-elegant"
          placeholder="(任意)開催場所を入力してください"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="event_date" className="text-elegant-dark text-lg flex items-center gap-2">
          <Calendar className="w-5 h-5 text-elegant" />
          開催時期
        </Label>
        <Input
          id="event_date"
          value={formData.event_date || ""}
          onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
          className="border-elegant/20 focus:border-elegant"
          placeholder="(任意)例: 毎年8月第一週末、2024年5月3日〜5日など"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="writer" className="text-elegant-dark text-lg flex items-center gap-2">
          <User className="w-5 h-5 text-elegant" />
          執筆者
        </Label>
        <Input
          id="writer"
          value={formData.writer || ""}
          onChange={(e) => setFormData({ ...formData, writer: e.target.value })}
          className="border-elegant/20 focus:border-elegant"
          placeholder="(任意)執筆者を入力してください"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="excerpt" className="text-elegant-dark text-lg">
          概要
        </Label>
        <Textarea
          id="excerpt"
          value={formData.excerpt}
          onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
          required
          className="border-elegant/20 focus:border-elegant min-h-[100px]"
          placeholder="記事の概要を入力してください（100文字程度）"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="content" className="text-elegant-dark text-lg">
          本文
        </Label>
        <Textarea
          id="content"
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          required
          className="border-elegant/20 focus:border-elegant min-h-[200px]"
          placeholder="記事の本文を入力してください"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="image_url" className="text-elegant-dark text-lg flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-elegant" />
          画像URL
        </Label>
        <Input
          id="image_url"
          value={formData.image_url || ""}
          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
          className="border-elegant/20 focus:border-elegant"
          placeholder="画像のURLを入力してください（任意）"
        />
      </div>

      <Button
        type="submit"
        disabled={isLoading}
        className="w-full bg-kawatteru-accent-purple hover:bg-kawatteru-accent-purple/90 text-white font-medium py-3 shadow-lg"
      >
        {isLoading ? "保存中..." : isSpecial ? "スペシャル記事を保存" : "記事を保存"}
      </Button>
    </form>
  );
};
